import { useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Lock, Key } from '@phosphor-icons/react';

/**
 * Dialog for legacy trips that don't have an owner password yet.
 * Prompts for the platform admin password to generate (via regenerate endpoint).
 * Shows email-sent confirmation after success — never shows plaintext.
 */
export default function GenerateOwnerPasswordDialog({ open, onOpenChange, onSuccess, tripCode }) {
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { emailStatus: 'sent' | 'failed', token }

  const handleGenerate = async (e) => {
    e?.preventDefault();
    if (!adminPassword.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post(`/trips/${tripCode}/regenerate-owner-password`, {
        current_password: adminPassword.trim(),
      });
      setResult({
        emailStatus: data.owner_password_email_status || 'failed',
        token: data.token,
      });
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      setError(detail === 'Invalid password' ? 'Invalid admin password.' : detail || 'Failed to generate password.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    const token = result?.token;
    setAdminPassword('');
    setResult(null);
    setError('');
    if (token) onSuccess(token);
    else onOpenChange(false);
  };

  const handleClose = () => {
    setAdminPassword('');
    setResult(null);
    setError('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!val) handleClose(); }}>
      <DialogContent className="sm:max-w-sm rounded-2xl" onPointerDownOutside={(e) => { if (result) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-[#1B4332]" weight="bold" />
            Generate Master Password
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <>
            <p className="text-sm text-[#455561]">
              This event doesn't have a master password yet. Enter the platform admin password to generate one — it will be emailed securely.
            </p>
            <form onSubmit={handleGenerate} className="space-y-3 mt-1">
              <Input
                type="password"
                placeholder="Platform admin password"
                value={adminPassword}
                onChange={(e) => { setAdminPassword(e.target.value); setError(''); }}
                className="h-12 rounded-xl text-base"
                autoFocus
                data-testid="generate-owner-admin-password-input"
              />
              {error && <p className="text-sm text-[#D32F2F]" data-testid="generate-owner-error">{error}</p>}
              <DialogFooter className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleClose} className="flex-1 rounded-xl">Cancel</Button>
                <Button
                  type="submit"
                  disabled={loading || !adminPassword.trim()}
                  className="flex-1 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl"
                  data-testid="generate-owner-submit-btn"
                >
                  {loading ? 'Generating...' : 'Generate'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="space-y-4">
            {result.emailStatus === 'sent' ? (
              <div className="bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl p-4 space-y-2" data-testid="generate-email-sent-panel">
                <p className="text-sm font-bold text-[#2E7D32]">Master password sent</p>
                <p className="text-sm text-[#2E7D32]">
                  Master password sent to <strong>acottrill@foretour.co.uk</strong>. Check your inbox — only needed to change scores or rounds after scoring has started.
                </p>
              </div>
            ) : (
              <div className="bg-[#FFF3E0] border border-[#FFE0B2] rounded-xl p-4 space-y-2" data-testid="generate-email-failed-panel">
                <p className="text-sm font-bold text-[#E59000]">Email delivery failed</p>
                <p className="text-sm text-[#E59000]">
                  Master password could not be emailed — contact support. The event has been created, but actions on scored rounds will need the admin regeneration path until this is resolved.
                </p>
              </div>
            )}
            <Button
              onClick={handleContinue}
              className="w-full h-12 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-bold"
              data-testid="generate-owner-continue-btn"
            >
              Continue
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
